import * as React from 'react';

import { ActionMenuItemClickedChildFunction, ActionMenuItemClickedTelemetryContext, CardPositionContext, ContentClickedChildFunction, ContentClickedTelemetryContext, ContentInfoContext, FavoriteToggleClickedChildFunction, FavoriteToggleClickedTelemetryContext, RelatedContentInfoContext } from '../../app/contexts/TelemetryContexts';
import { ConnectedContentCardWrapper } from '../../app/spaces/CardView/ConnectedContentCardWrapper';
import { SpaceInfo } from '../../app/state/SpaceInfo';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { ContentLocation, FavoriteToggleType } from '../../common/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '../../common/telemetryEvents/util/ContentTelemetry';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../ts/models/resourceInfoParts';
import { ChannelStyles } from '../styles/ChannelStyles';
import { HomeStyles } from '../styles/HomeStyles';
import { ChannelTypes } from '../types/ChannelTypes';
import { ChannelHeader } from './ChannelHeader';

export interface ChannelProps {
  readonly channelType: ChannelTypes;
  readonly title: string;
  readonly list: ReadonlyArray<IContentInfo>;
  readonly seeAllUrl: string;
  readonly seeAllAltText: string;
  readonly isFetching: boolean;
  readonly maxCardsPerRow: number;
  readonly numRows: number;
  readonly spaceInfo: SpaceInfo;
}

export interface ChannelOwnProps {
  readonly maxCardsPerRow: number;
}

export class Channel extends React.PureComponent<ChannelProps> {
  private channelMaxHeight: React.CSSProperties;

  constructor(props: ChannelProps) {
    super(props);

    const maxRowHeight = this.props.numRows * ChannelStyles.cardHeight;
    this.channelMaxHeight = { maxHeight: maxRowHeight};
  }

  render() {
    const maxNumCards = this.props.maxCardsPerRow * this.props.numRows;
    const cards = this.props.list.slice(0, maxNumCards).map((contentInfo: IContentInfo, idx: number) => {
      return (
        <React.Fragment key={`${contentInfo.contentType}:${contentInfo.id}:${this.props.channelType}`}>
          <ContentInfoContext.Provider value={contentInfo}>
            <CardPositionContext.Provider value={idx + 1}>
              <div className={ChannelStyles.cardDimensions}>
                <ConnectedContentCardWrapper content={contentInfo} showActionMenu={true} />
              </div >
            </CardPositionContext.Provider>
          </ContentInfoContext.Provider>
        </React.Fragment>
      );
    });

    return (
      !this.props.isFetching && this.props.list.length > 0 &&
      <ContentClickedTelemetryContext.Provider value={generateContentClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
        <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
          <ActionMenuItemClickedTelemetryContext.Provider value={generateActionMenuItemClickedTelemetryContext(this.props.spaceInfo, this.props.channelType, this.props.list)}>
            <section className={HomeStyles.contentSection} data-tb-test-id={`${this.props.channelType}-channel`}>
              <ChannelHeader
                channelType={this.props.channelType}
                title={this.props.title}
                seeAllUrl={this.props.seeAllUrl}
                seeAllAltText={this.props.seeAllAltText}
              />
              <div className={ChannelStyles.cardsContainer} style={this.channelMaxHeight}>
                {cards}
              </div>
            </section>
          </ActionMenuItemClickedTelemetryContext.Provider>
        </FavoriteToggleClickedTelemetryContext.Provider>
      </ContentClickedTelemetryContext.Provider>
    );
  }
}

// Telemetry Handler context values

export function generateContentClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function ContentClickedTelemetryContext(childFunction: ContentClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <RelatedContentInfoContext.Consumer>
            {relatedContentInfo =>
              <CardPositionContext.Consumer>
                {cardIndex =>
                  childFunction(() => {
                    const eventHandler = generateTelemetryEventHandler(
                      TelemetryEvents.ContentClicked,
                      spaceInfo,
                      allContents,
                      contentInfo,
                      ContentLocation.Home,
                      cardIndex,
                      {
                        homeChannel: channelType,
                        relatedContentId: relatedContentInfo && relatedContentInfo.relatedContentId,
                        // Known bug on TS prevents using the commented line and we have to use an if statemnte
                        // https://github.com/microsoft/TypeScript/issues/32455
                        // relatedContentType: relatedContentInfo && relatedContentInfo.relatedContentType,
                        relatedContentType: relatedContentInfo? relatedContentInfo.relatedContentType : null
                      }
                    );
                    eventHandler();
                  })
                }
              </CardPositionContext.Consumer>
            }
          </RelatedContentInfoContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <CardPositionContext.Consumer>
            {cardIndex =>
              childFunction((toggleType: FavoriteToggleType) => {
                const eventHandler = generateTelemetryEventHandler(
                  TelemetryEvents.FavoriteToggleClicked,
                  spaceInfo,
                  allContents,
                  contentInfo,
                  ContentLocation.Home,
                  cardIndex,
                  {homeChannel: channelType}
                );
                eventHandler(toggleType);
              })
            }
          </CardPositionContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}

export function generateActionMenuItemClickedTelemetryContext(spaceInfo: SpaceInfo, channelType: ChannelTypes, allContents: ReadonlyArray<IExplorableItemTypeInfo>) {
  return function ActionMenuItemClickedTelemetryContext(childFunction: ActionMenuItemClickedChildFunction) {
    return (
      <ContentInfoContext.Consumer>
        {contentInfo =>
          <CardPositionContext.Consumer>
            {cardIndex =>
              childFunction((actionType: string) => {
                const eventHandler = generateTelemetryEventHandler(
                  TelemetryEvents.ActionMenuItemClicked,
                  spaceInfo,
                  allContents,
                  contentInfo,
                  ContentLocation.Home,
                  cardIndex,
                  {homeChannel: channelType}
                );
                eventHandler(actionType);
              })
            }
          </CardPositionContext.Consumer>
        }
      </ContentInfoContext.Consumer>
    );
  };
}
